import { authProvider } from "./provider";
import { authStorage } from "./storage";
import { isJwtExpired } from "./session";

class AuthService {
  private refreshPromise: Promise<string> | null = null;

  async login(): Promise<string> {
    const token = await authProvider.signIn();
    await authStorage.set(token);
    return token;
  }

  async logout(): Promise<void> {
    await authStorage.clear();
  }

  async getStoredToken(): Promise<string | undefined> {
    const token = await authStorage.get();
    if (!token) return undefined;

    if (isJwtExpired(token)) {
      await authStorage.clear();
      return undefined;
    }

    return token;
  }

  async refresh(): Promise<string> {
    if (!this.refreshPromise) {
      this.refreshPromise = authProvider
        .refreshSilently()
        .then(async (token) => {
          await authStorage.set(token);
          return token;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  async ensureSession(): Promise<string | undefined> {
    const existing = await this.getStoredToken();
    if (existing) return existing;

    try {
      return await this.refresh();
    } catch {
      await this.logout();
      return undefined;
    }
  }

  async invalidate(): Promise<void> {
    await this.logout();
  }
}

export const authService = new AuthService();